import {
  addHours,
  differenceInYears,
  format,
  isThisYear,
  isToday,
  isValid,
  isYesterday,
  parse,
  parseISO,
} from 'date-fns'
import { TZDate } from '@date-fns/tz'

/**
 * The ISO 8601 calendar-date format, in date-fns tokens.
 *
 * Worth sharing because the format is fixed rather than chosen: it is what the
 * API's `date` fields carry and what `<input type="date">` reads and writes.
 */
export const ISO_DATE_FORMAT = 'yyyy-MM-dd'

/**
 * The clinic's timezone. Shift and due-date arithmetic is defined in it rather
 * than in the viewer's zone, so an ops user in any timezone reads the same
 * schedule. `US/Eastern` is an alias for it and resolves to the same instants.
 */
export const EASTERN_TIME_ZONE = 'America/New_York'

const CARRIES_OFFSET = /(?:Z|[+-]\d{2}:?\d{2})$/

/**
 * Reads a datetime string in `timeZone`: a string carrying an offset names an
 * absolute instant, and one without it names wall-clock time in that zone.
 *
 * That distinction is load-bearing — API values arrive with an offset, while
 * `<input type="datetime-local">` hands back bare wall clock — and neither
 * obvious alternative makes it. `parseISO` reads a bare string in the *viewer's*
 * zone, and `new TZDate(string, zone)` parses in the viewer's zone too, tagging
 * `zone` for display only.
 */
export const zonedDateTime = (value: string, timeZone: string = EASTERN_TIME_ZONE): TZDate => {
  if (CARRIES_OFFSET.test(value)) return new TZDate(parseISO(value), timeZone)

  const [datePart = '', timePart = ''] = value.trim().split(/[T ]/)
  const [year = NaN, month = NaN, day = NaN] = datePart.split('-').map(Number)
  const [hours = 0, minutes = 0, seconds = 0] = timePart.split(':').map(Number)
  return TZDate.tz(timeZone, year, month - 1, day, hours, minutes, seconds)
}

/**
 * Formats an instant as wall-clock time in `timeZone`. A bare string is read in
 * the viewer's zone first, so pass one only when that is what you mean.
 *
 * Returns '' rather than throwing when there is no usable date. Callers render
 * this straight into date inputs, and an absent bound is an ordinary state there
 * — `format` would take the whole editor down on it.
 */
export const formatInZone = (
  value: string | Date | null | undefined,
  pattern: string,
  timeZone: string = EASTERN_TIME_ZONE,
) => {
  if (!value) return ''
  const instant = new TZDate(typeof value === 'string' ? parseISO(value) : value, timeZone)
  return isValid(instant) ? format(instant, pattern) : ''
}

/**
 * Validates that a value is a valid ISO 8601 date string in YYYY-MM-DD format.
 * Checks shape, that the date actually exists, and year range (1900-2100).
 *
 * @param value - The string to validate
 * @returns A type guard indicating whether the value is a valid ISO date string
 */
export const isValidIsoDate = (value: string | null | undefined): value is string => {
  if (!value) return false
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false
  const parsed = parse(value, ISO_DATE_FORMAT, new Date())
  if (!isValid(parsed)) return false
  const year = parsed.getFullYear()
  return year >= 1900 && year <= 2100
}

/**
 * Format a datetime string as "MMM. d, yyyy" (e.g., "Jan. 15, 2025"), or as an
 * empty string when the value is not a parseable date.
 */
export const formatDisplayDate = (dateTime: string) => formatApiDate(dateTime, 'MMM. d, yyyy') ?? ''

export const formatDateWithDay = (date: Date) => {
  return format(new Date(date), "EEE. MMM d, yyyy 'at' h:mm a")
}

export const formatReadAt = (date: Date) => {
  if (isToday(date)) {
    return `Today at ${format(date, 'h:mm a')}`
  } else if (isYesterday(date)) {
    return `Yesterday at ${format(date, 'h:mm a')}`
  } else if (isThisYear(date)) {
    return format(date, 'EEE, MMM d')
  } else {
    return format(date, 'EEE, MMM d, yyyy')
  }
}

/**
 * Returns the given date formatted in the RFC 3339 "date-time" format.
 * See https://datatracker.ietf.org/doc/html/rfc3339#section-5.6
 *
 * The offset token is `xxx`, not `XXX`, so a zero offset is written `+00:00`
 * rather than `Z`. date-fns' own `formatRFC3339` writes `Z` there too — RFC 3339
 * allows either, but this value goes out as an API query param, so it keeps the
 * always-numeric offset the endpoint has always received.
 */
const RFC3339_DATE_TIME = "yyyy-MM-dd'T'HH:mm:ssxxx"

export const formatRFC3339DateTime = (date: Date) => {
  return format(new Date(date), RFC3339_DATE_TIME)
}

/**
 * RFC 3339 for a value that may be absent or unreadable — a date or time input the
 * user has cleared, typically.
 *
 * Returns '' rather than throwing, which lets the request be assembled and sent.
 * The callers build payloads whose own layers already handle an absent date —
 * `TaskService.createTask`, for one, drops a falsy `due_date` from the body — and
 * throwing here pre-empts that: those handlers are fire-and-forget, or build the
 * payload before entering their try block, so nothing surfaces to the user.
 */
export const formatRFC3339DateTimeOrEmpty = (value: string | Date | null | undefined) =>
  formatApiDate(value, RFC3339_DATE_TIME) ?? ''

/**
 * Takes the calendar date of `date` in the viewer's own timezone and returns the
 * instant 19 hours after midnight of that date in US/Eastern, as an ISO string
 * with a Z suffix for the API.
 *
 * That is 7PM Eastern on all but two days a year: the 19 hours are absolute, so
 * on the spring-forward date the result is 8PM Eastern and on the fall-back date
 * it is 6PM. Preserved deliberately — every due date the CRM has ever written
 * used this arithmetic.
 */
export const setDueDateToEOD = (date: string | Date) => {
  // Most callers pass a bare `yyyy-MM-dd` string. Handing that to `format`
  // directly would coerce it with `new Date()`, which reads a date-only string as
  // UTC midnight and so lands on the previous day anywhere west of Greenwich;
  // `parseISO` reads it as local midnight, the calendar date the caller meant.
  const day = typeof date === 'string' ? parseISO(date) : date
  const easternMidnight = zonedDateTime(format(day, ISO_DATE_FORMAT))
  // TZDate#toISOString renders in its own zone, so go through the timestamp.
  return new Date(addHours(easternMidnight, 19).getTime()).toISOString()
}

/**
 * Format an API date string for display, or return null when the value is not a
 * parseable date.
 *
 * Needed for fields the contract types as a bare `string` rather than
 * `format: date-time` — Ribbon's `added_on` is one, and it reaches us as an
 * untyped third-party value. `format` throws a RangeError on an invalid date, so
 * an unparseable value would otherwise take down the whole surrounding subtree.
 */
export const formatApiDate = (
  value: string | Date | null | undefined,
  dateFormat: string,
): string | null => {
  if (!value) return null
  const parsed = typeof value === 'string' ? parseISO(value) : value
  return isValid(parsed) ? format(parsed, dateFormat) : null
}

// Shared function for returning proper patient age
// Returns NaN for a missing or unparseable date of birth: `dob` is nullable in
// the contract, `parse` throws on null, and the call sites render this unguarded.
export const getUserAge = (dob: string | null | undefined) =>
  dob ? differenceInYears(new Date(), parse(dob, ISO_DATE_FORMAT, new Date())) : NaN
export const formatUserDateOfBirth = (dob: string | null) => formatApiDate(dob, 'MM/dd/yyyy') ?? ''
